"use client";

import {useTranslation} from "react-i18next";
import Icon, {ICON_SIZES} from "@/components/library/Icon";
import {NodeDefinitionIcon} from "@/components/workflows/utils/components/NodeDefinitionIcon";
import type {PublicationStep, workflowPublicationSummary} from "./workflowPublicationSummary";

export default function WorkflowPublicationSteps({summary}: {summary: ReturnType<typeof workflowPublicationSummary>}) {
   const {t} = useTranslation("ask");
   return <section className="flex flex-col gap-1.5 px-4 pb-3.5" aria-label={t("toolApproval.workflow.steps")}>
      <h5 className="text-xs font-medium text-[var(--approval-muted)]">{t("publishWorkflow.steps")}</h5>
      <ul className="overflow-hidden rounded-lg border border-[var(--approval-border)]"><StepItems steps={summary.steps} /></ul>
      <details className="group/connections pt-1 text-xs leading-[18px] text-[var(--approval-body)]">
         <summary className="flex cursor-pointer list-none items-center gap-1.5 font-medium [&::-webkit-details-marker]:hidden"><Icon icon="chevron-right" className="h-3 w-3 shrink-0 transition-transform group-open/connections:rotate-90 motion-reduce:transition-none" />
            {t("publishWorkflow.connections", {count: summary.connections.length})}
         </summary>
         <div className="pl-[18px] pt-1">{summary.connections.map(connection => <p key={connection}>{connection}</p>)}</div>
      </details>
   </section>;
}

function StepItems({steps}: {steps: PublicationStep[]}) {
   const {t} = useTranslation("ask");
   return steps.map((step, index) => <li className="[&+li]:border-t [&+li]:border-[var(--approval-track)]" key={`${step.id}:${index}`}>
      <div className="flex items-center gap-2.5 px-3 py-2 text-[13px] leading-[18px]">
         <span className="flex h-6 w-6 shrink-0 items-center justify-center rounded-md bg-[var(--approval-track)] data-[type=start]:bg-[var(--approval-success-bg)] data-[type=start]:text-[var(--approval-success)] data-[type=finish]:bg-[var(--approval-error-bg)] data-[type=finish]:text-[var(--approval-error)]" data-type={step.type}>
            {step.definition ? <NodeDefinitionIcon node={step.definition} size={ICON_SIZES.XS} /> : <Icon icon="gear" className="h-3 w-3" />}
         </span>
         <span className="min-w-0 flex-1 [overflow-wrap:anywhere]">{step.name}</span>
         <span className="text-xs text-[var(--approval-muted)]">{t(`publishWorkflow.stepTypes.${step.type}`)}</span>
      </div>
      {step.children.length > 0 && <ul className="mb-2 ml-4 border-l border-[var(--approval-track)]" aria-label={step.name}><StepItems steps={step.children} /></ul>}
   </li>);
}
